import Expander from 'components/utils/Expander'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineDollarSignIcon from 'LuxKit/Icons/line/LineDollarSignIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { EmptyArray, groupBy, skip, sortBy, take } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { rem } from 'polished'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { FLEXIBILITY_INCLUSIONS } from 'constants/flight'
import { hasNoFlexibilityFareFeatures } from 'lib/flights/flightUtils'
import { FormattedMessage, useIntl } from 'react-intl'

/** Fresh elements per row — reusing one React element from a map for multiple rows breaks reconciliation. */
function fareFeatureStatusIcon(status: App.FareFamilyFeatureStatus): React.ReactElement {
  switch (status) {
    case 'CHARGED':
      return <LineDollarSignIcon colour="highlight-primary-dark" />
    case 'INCLUDED':
      return <LineCheckIcon colour="highlight-primary-dark" />
    case 'NOT_OFFERED':
      return <LineTimesIcon />
    default:
      return <LineTimesIcon />
  }
}

const ProtectedFareRuleSpacer = styled.div`
  height: ${rem(16)};
`

const MAX_EXTRA_ITEMS = 2
const MIN_HIDDEN_ITEMS_FOR_TOGGLE = 2

export interface FareFamilyFareFeatureDetailsProps {
  features?: Array<App.FareFamilyFeature>
  fareFamily: App.FlightFareFamily
  showMore?: boolean
  onShowMore?: () => void
  onShowFareRules?: (urls: string) => void
}

function FareFamilyFareFeatureDetails(props: FareFamilyFareFeatureDetailsProps) {
  const {
    features = EmptyArray,
    fareFamily,
    showMore,
    onShowMore = noop,
    onShowFareRules = noop,
  } = props

  const intl = useIntl()

  const showLessLabel = intl.formatMessage({
    id: '2_-wo1GfttsJOv6p_CUf',
    defaultMessage: 'Show less',
    description: 'Hyperlink text to collapse extra fare features in the flights section',
  })

  const showMoreLabel = intl.formatMessage({
    id: 'Q-NkmmNfa1Y3i5CgGSY-',
    defaultMessage: 'Show more',
    description: 'Hyperlink text to expand extra fare features in the flights section',
  })

  const groupedFeatures = useMemo(() => {
    const sortedFeatures = sortBy(features, feature => feature.group, 'asc')
    const grouped = groupBy(sortedFeatures, val => val.category)
    const baggageData = grouped.get('baggage') || []
    const fareRulesData = grouped.get('fare_rules') || []
    const extraData = grouped.get('extra') || []

    return [
      {
        title: intl.formatMessage({
          id: 'vduDCPDTCbIUdBptEBRD',
          defaultMessage: 'Baggage',
          description: 'Section title for baggage features in fare family option',
        }),
        id: 'baggage',
        data: baggageData,
      },
      {
        title: intl.formatMessage({
          id: 'kR-4EM0QsH4kmMHBW4d8',
          defaultMessage: 'Fare rules',
          description: 'Section title for fare rules in fare family option',
        }),
        id: 'fare_rules',
        data: fareRulesData,
      },
      {
        title: intl.formatMessage({
          id: 'y1euztw6VJ3qQyJbp4bp',
          defaultMessage: 'Seats, meals and more',
          description: 'Section title for extra features in fare family option',
        }),
        id: 'extra',
        data: extraData,
      },
    ].filter(section => section.data.length > 0)
  }, [features, intl])

  const onViewFareRules = useCallback(() => {
    onShowFareRules(fareFamily.fareRulesUrl)
  }, [fareFamily, onShowFareRules])

  const extraFeaturesLength = useMemo(() => {
    return groupedFeatures.find(feature => feature.id === 'extra')?.data.length ?? 0
  }, [groupedFeatures])

  const isProtectedFareRulesSmallest = useCallback((feature: typeof groupedFeatures[0], family: App.FlightFareFamily) => {
    if (feature.id !== 'fare_rules' || !family.withEmbeddedInsurance) return

    const baggageFeaturesLenght = groupedFeatures.find(f => f.id === 'baggage')?.data.length ?? 0
    const fareRulesFeaturesLenght = groupedFeatures.find(f => f.id === 'fare_rules')?.data.length ?? 0
    const extraFeaturesLenght = groupedFeatures.find(f => f.id === 'extra')?.data.length ?? 0

    const isFareRulesSmaller = fareRulesFeaturesLenght < baggageFeaturesLenght || fareRulesFeaturesLenght < extraFeaturesLenght

    return isFareRulesSmaller
  }, [groupedFeatures])

  const formatFeatureDescription = useCallback((feature: App.FareFamilyFeature) => {
    if (feature.type && FLEXIBILITY_INCLUSIONS.has(feature.type)) {
      return intl.formatMessage(
        {
          id: 'cXGc_LQLcCqiKn1rw0CQ',
          defaultMessage: '{description} *',
          description: 'Fare feature description with asterisk indicating flexibility inclusion',
        },
        { description: feature.description },
      )
    }
    return feature.description
  }, [intl])

  return (
    <>
      {groupedFeatures.map(feature => (
        <React.Fragment key={feature.id}>
          <BodyText variant="small" weight="bold">{feature.title}</BodyText>

          {feature.id !== 'extra' && (
            <VerticalSpacer gap={8}>
              {feature.data.map(row =>
                <BodyTextBlock
                  variant="small"
                  key={row.code}
                  startIcon={fareFeatureStatusIcon(row.status)}
                  colour={row.status === 'NOT_OFFERED' ? 'neutral-four' : undefined}
                >
                  {formatFeatureDescription(row)}
                </BodyTextBlock>,
              )}
              {isProtectedFareRulesSmallest(feature, fareFamily) && (
                <ProtectedFareRuleSpacer />
              )}
            </VerticalSpacer>
          )}

          {feature.id === 'extra' && (() => {
            const hiddenItemsCount = feature.data.length - MAX_EXTRA_ITEMS
            const shouldShowToggle = hiddenItemsCount >= MIN_HIDDEN_ITEMS_FOR_TOGGLE
            const initialItemsToShow = shouldShowToggle ? MAX_EXTRA_ITEMS : feature.data.length

            return (
              <VerticalSpacer gap={8}>
                {take(feature.data, initialItemsToShow).map(f =>
                  <BodyTextBlock
                    variant="small"
                    key={f.code}
                    startIcon={fareFeatureStatusIcon(f.status)}
                    colour={f.status === 'NOT_OFFERED' ? 'neutral-four' : undefined}
                  >
                    {formatFeatureDescription(f)}
                  </BodyTextBlock>,
                )}

                {shouldShowToggle && (
                  <Expander expanded={!!showMore}>
                    <VerticalSpacer gap={8}>
                      {skip(feature.data, MAX_EXTRA_ITEMS).map(f =>
                        <BodyTextBlock
                          variant="small"
                          key={f.code}
                          startIcon={fareFeatureStatusIcon(f.status)}
                          colour={f.status === 'NOT_OFFERED' ? 'neutral-four' : undefined}
                        >
                          {formatFeatureDescription(f)}
                        </BodyTextBlock>,
                      )}
                    </VerticalSpacer>
                  </Expander>
                )}
              </VerticalSpacer>
            )
          })()}
        </React.Fragment>
      ))}

      {extraFeaturesLength - MAX_EXTRA_ITEMS >= MIN_HIDDEN_ITEMS_FOR_TOGGLE && (
        <TextLink
          onClick={onShowMore}
          endIcon={showMore ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
          size="caption"
        >
          {showMore ? showLessLabel : showMoreLabel}
        </TextLink>
      )}
      {hasNoFlexibilityFareFeatures(features) && (
        <BodyTextBlock
          colour="neutral-three"
          variant="small"
        >
          <FormattedMessage
            id="BwnEZaenmkHhScFCS2u-"
            defaultMessage="View <link>fare rules</link> for changes, cancellation & refunds policy *"
            description="Prompt to view fare rules for changes, cancellation and refunds policy"
            values={{
              link: (chunks: React.ReactNode) => <TextLink key="fare-rules-inline" size="small" weight="regular" onClick={onViewFareRules}>{chunks}</TextLink>,
            }}
          />
        </BodyTextBlock>
      )}
    </>
  )
}

export default FareFamilyFareFeatureDetails
